import styled from 'styled-components';
import { useContext, useLayoutEffect, useMemo, useRef } from 'react';
import * as echarts from 'echarts';
import 'echarts/theme/v5.js';
import { usePrevious, useResizeObserver } from '@ardoq/hooks';
import { ChartAnimationDisabledContext } from './chartAnimation';

// Expose echarts to browser context for plugins
(globalThis as typeof globalThis & { echarts: typeof echarts }).echarts =
  echarts;

type LegendPageInfo = {
  pageNextDataIndex: number | null;
  pagePrevDataIndex: number | null;
};

type ScrollableLegendView = {
  _getPageInfo?: (legendModel: object) => LegendPageInfo;
};

type EChartsLegendInternals = {
  getModel: () => {
    getComponent: (mainType: string, index: number) => object | undefined;
  };
  getViewOfComponentModel: (model: object) => ScrollableLegendView | undefined;
};

// echarts' scroll legend exposes its current page boundaries only through the
// internal view (`_getPageInfo`); there is no public API for it. We read it so
// we can leave the legend untouched when the hovered slice's entry is already
// on the visible page, and only scroll to it when it lives on another page.
// Legend rows are uniform height, so pages start at fixed multiples of the page
// size — we scroll to that boundary rather than to the entry itself, which
// would re-anchor the page grid and invent an extra page.
export const scrollLegendEntryIntoView = (
  chart: echarts.ECharts,
  legendNames: string[],
  hoveredName: string
): void => {
  const targetIndex = legendNames.indexOf(hoveredName);
  if (targetIndex < 0) return;

  const internals = chart as unknown as EChartsLegendInternals;
  const legendModel = internals.getModel().getComponent('legend', 0);
  if (!legendModel) return;

  const legendView = internals.getViewOfComponentModel(legendModel);
  if (!legendView?._getPageInfo) return;

  const option = chart.getOption();
  const legendOptions = Array.isArray(option.legend) ? option.legend : [];
  const currentTopIndex = Number(legendOptions[0]?.scrollDataIndex) || 0;
  const { pageNextDataIndex, pagePrevDataIndex } =
    legendView._getPageInfo(legendModel);
  const pageEndIndex = pageNextDataIndex ?? Number.POSITIVE_INFINITY;

  const isOnVisiblePage =
    targetIndex >= currentTopIndex && targetIndex < pageEndIndex;
  if (isOnVisiblePage) return;

  const pageSize =
    pageNextDataIndex !== null
      ? pageNextDataIndex - currentTopIndex
      : pagePrevDataIndex !== null
        ? currentTopIndex - pagePrevDataIndex
        : 0;
  if (pageSize <= 0) return;

  const targetPageTopIndex = Math.floor(targetIndex / pageSize) * pageSize;
  chart.dispatchAction({
    type: 'legendScroll',
    scrollDataIndex: targetPageTopIndex,
  });
};

export const toAnimatedChartOptions = (
  chartOptions: echarts.EChartsOption,
  isAnimationDisabled: boolean
): echarts.EChartsOption => ({
  ...chartOptions,
  animation: isAnimationDisabled ? false : (chartOptions.animation ?? true),
});

const useChart = (
  chartOptions: echarts.EChartsOption,
  {
    clickHandler,
    legendHandler,
    getResizeOptions,
    legendNames,
  }: {
    clickHandler?: PieChartClickHandler | StackedBarChartClickHandler | null;
    legendHandler?: () => void;
    getResizeOptions?: (size: ContainerSize) => echarts.EChartsOption | null;
    legendNames?: string[];
  } = {}
) => {
  const chart = useRef<echarts.ECharts | null>(null);
  const chartRef = useRef(null);
  const previousSeriesLength = usePrevious(
    Array.isArray(chartOptions.series) ? chartOptions.series.length : 0
  );

  useLayoutEffect(() => {
    if (chartRef.current && !chart.current) {
      /** 🦖 sticking to v5 theme. I have upgraded to v6 to fix a vulnerability, not to change UX. perhaps some future visitors will remove the v5 argument and review the visual improvements, which will show up in the chromatic tests. 🏛️ */
      const theme = 'v5';
      chart.current = echarts.init(chartRef.current, theme);
    }
  }, [chart, chartRef]);

  useLayoutEffect(() => {
    if (!chart.current) return;

    const setOptionArgs =
      Array.isArray(chartOptions.series) &&
      previousSeriesLength &&
      chartOptions.series.length < previousSeriesLength
        ? { replaceMerge: ['series'] }
        : undefined;
    // There is a bug in the echarts library when you decrease the amount of series i.e. by changing the numbers of stacks in a stacked bar chart that causes the old series to stay there when
    // they should have been removed. Adding this arg to setOptions refreshes the chart with the new series.
    // https://github.com/apache/echarts/issues/6202

    chart.current.setOption(chartOptions, setOptionArgs);

    // Apply size-dependent options right after base options so the chart
    // renders with correct dimensions before the browser paints.
    if (getResizeOptions && chartRef.current) {
      const el = chartRef.current as HTMLElement;
      const opts = getResizeOptions({
        width: el.clientWidth,
        height: el.clientHeight,
      });
      if (opts) chart.current.setOption(opts);
    }

    if (clickHandler)
      // I'm casting clickHandler here even though it's not always VoidFunction, but echarts complains about the type for some reason even though it's correct
      chart.current.on('click', 'series', clickHandler as VoidFunction);
    if (legendHandler) chart.current.on('legendselectchanged', legendHandler);

    const hoverHandler =
      legendNames && legendNames.length > 0
        ? (params: { name?: string | number }) => {
            if (chart.current && params.name !== undefined)
              scrollLegendEntryIntoView(
                chart.current,
                legendNames,
                String(params.name)
              );
          }
        : null;
    if (hoverHandler)
      chart.current.on('mouseover', 'series', hoverHandler as VoidFunction);

    return () => {
      if (chart.current) {
        if (clickHandler) chart.current.off('click', clickHandler);
        if (legendHandler)
          chart.current.off('legendselectchanged', legendHandler);
        if (hoverHandler) chart.current.off('mouseover', hoverHandler);
      }
    };
  }, [
    chart,
    chartOptions,
    clickHandler,
    legendHandler,
    previousSeriesLength,
    getResizeOptions,
    legendNames,
  ]);

  return { chartRef, chart };
};

const ChartContainer = styled.div.attrs({ role: 'img' })`
  width: 100%;
  height: 100%;
`;

export type PieChartClickHandler = (clickParams: {
  data: {
    name: string;
    isOtherSegment: boolean;
  };
}) => void;

type StackedBarChartClickHandler = (clickParams: {
  seriesName: string;
  seriesIndex: number;
}) => void;

export type ContainerSize = { width: number; height: number };

type ChartWidgetProps = {
  chartOptions: echarts.EChartsOption;
  clickHandler?: PieChartClickHandler;
  /** Called on resize with container dimensions. Return partial echarts options
   *  to merge directly on the instance — bypasses React so handlers and
   *  interaction state are preserved. */
  getResizeOptions?: (size: ContainerSize) => echarts.EChartsOption | null;
  /** When set, hovering a series item scrolls the (scroll-type) legend to the
   *  page holding the matching entry. Ordered to match `legend.data`. */
  legendNames?: string[];
};

const Chart = ({
  chartOptions,
  clickHandler,
  getResizeOptions,
  legendNames,
}: ChartWidgetProps) => {
  const isAnimationDisabled = useContext(ChartAnimationDisabledContext);
  const animatedChartOptions = useMemo(
    () => toAnimatedChartOptions(chartOptions, isAnimationDisabled),
    [chartOptions, isAnimationDisabled]
  );
  const { chartRef, chart } = useChart(animatedChartOptions, {
    clickHandler,
    getResizeOptions,
    legendNames,
  });
  useResizeObserver(chartRef, () => {
    chart.current?.resize();
    if (getResizeOptions && chart.current && chartRef.current) {
      const el = chartRef.current as HTMLElement;
      const opts = getResizeOptions({
        width: el.clientWidth,
        height: el.clientHeight,
      });
      if (opts) chart.current.setOption(opts);
    }
  });

  return <ChartContainer ref={chartRef} />;
};
export { Chart };
